// IP parsing, validation, and reserved-range classification.
//
// PRIVACY NOTE: nothing in this file touches the network. Every function here
// answers from the address itself, so a private/bogon IP is identified without
// a single request going out. The networked halves of the IP Address
// Intelligence tool live in ipIntelDatasets.js (public lists, target IP never
// sent) and ipIntelLive.js (per-IP lookups, explicitly opted into by the user).

/* global BigInt */

const IPV4_RE = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

export function isValidIpv4(ip) {
  const m = IPV4_RE.exec(String(ip).trim());
  if (!m) return false;
  for (let i = 1; i <= 4; i++) {
    const oct = m[i];
    // Reject leading zeros ("08" is ambiguous octal in some parsers).
    if (oct.length > 1 && oct[0] === '0') return false;
    if (Number(oct) > 255) return false;
  }
  return true;
}

export function isValidIpv6(ip) {
  const s = String(ip).trim();
  if (!s.includes(':')) return false;
  if ((s.match(/::/g) || []).length > 1) return false;

  // An embedded IPv4 tail ("::ffff:192.0.2.1") counts as two groups.
  let head = s;
  let tailGroups = 0;
  const lastColon = s.lastIndexOf(':');
  const tail = s.slice(lastColon + 1);
  if (tail.includes('.')) {
    if (!isValidIpv4(tail)) return false;
    head = s.slice(0, lastColon);
    tailGroups = 2;
  }

  const hasElision = head.includes('::');
  const parts = head.split('::');
  if (parts.length > 2) return false;

  const groups = [];
  for (const part of parts) {
    if (part === '') continue;
    for (const g of part.split(':')) {
      if (!/^[0-9a-fA-F]{1,4}$/.test(g)) return false;
      groups.push(g);
    }
  }

  const total = groups.length + tailGroups;
  return hasElision ? total <= 7 : total === 8;
}

export function isValidIp(ip) {
  return isValidIpv4(ip) || isValidIpv6(ip);
}

// Dotted quad -> unsigned 32-bit integer. Returns null for anything else.
export function ipToUint32(ip) {
  const m = IPV4_RE.exec(String(ip).trim());
  if (!m) return null;
  const a = Number(m[1]), b = Number(m[2]), c = Number(m[3]), d = Number(m[4]);
  if (a > 255 || b > 255 || c > 255 || d > 255) return null;
  // >>> 0 keeps it unsigned; a plain << would go negative above 127.x.
  return ((a << 24) | (b << 16) | (c << 8) | d) >>> 0;
}

export function uint32ToIp(n) {
  return [(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255].join('.');
}

// "1.2.3.0/24" -> [firstUint32, lastUint32], or null if unparseable.
export function cidrToRange(cidr) {
  const [addr, bitsRaw] = String(cidr).trim().split('/');
  const base = ipToUint32(addr);
  if (base === null) return null;
  const bits = bitsRaw === undefined ? 32 : Number(bitsRaw);
  if (!Number.isInteger(bits) || bits < 0 || bits > 32) return null;
  if (bits === 0) return [0, 0xffffffff];
  const mask = (0xffffffff << (32 - bits)) >>> 0;
  const start = (base & mask) >>> 0;
  const end = (start | (~mask >>> 0)) >>> 0;
  return [start, end];
}

// Full 128-bit value as a BigInt. Kept separate from the IPv4 uint32 helpers
// above because only one dataset in this tool (Apple's Private Relay egress
// ranges, in ipIntelDatasets.js) is wide enough to need IPv6 CIDR matching —
// everything else stays on the cheaper 32-bit path.
export function ipv6ToBigInt(ip) {
  const s = String(ip).trim();
  if (!isValidIpv6(s)) return null;

  let head = s;
  let tailValue = null;
  const lastColon = s.lastIndexOf(':');
  const tail = s.slice(lastColon + 1);
  if (tail.includes('.')) {
    const v4 = ipToUint32(tail);
    if (v4 === null) return null;
    head = s.slice(0, lastColon);
    tailValue = BigInt(v4);
  }

  const parts = head.split('::');
  const left = parts[0] ? parts[0].split(':') : [];
  const right = parts.length > 1 ? (parts[1] ? parts[1].split(':') : []) : null;
  const groups = right === null ? left : [
    ...left,
    ...Array((tailValue !== null ? 6 : 8) - left.length - right.length).fill('0'),
    ...right,
  ];

  let value = 0n;
  for (const g of groups) value = (value << 16n) | BigInt(parseInt(g, 16));
  if (tailValue !== null) value = (value << 32n) | tailValue;
  return value;
}

const MAX_V6 = (1n << 128n) - 1n;

// "2001:db8::/32" -> [firstBigInt, lastBigInt], or null if unparseable.
export function cidrToRangeV6(cidr) {
  const [addr, bitsRaw] = String(cidr).trim().split('/');
  const base = ipv6ToBigInt(addr);
  if (base === null) return null;
  const bits = bitsRaw === undefined ? 128 : Number(bitsRaw);
  if (!Number.isInteger(bits) || bits < 0 || bits > 128) return null;
  const mask = (MAX_V6 << BigInt(128 - bits)) & MAX_V6;
  const start = base & mask;
  const end = start | (MAX_V6 ^ mask);
  return [start, end];
}

// Reverse-DNS label for a v4 address: 8.8.8.8 -> 8.8.8.8.in-addr.arpa
export function reverseArpaName(ip) {
  const m = IPV4_RE.exec(String(ip).trim());
  if (!m) return null;
  return `${m[4]}.${m[3]}.${m[2]}.${m[1]}.in-addr.arpa`;
}

// Team Cymru wants the same reversed-octet form, without the .arpa suffix.
export function reversedOctets(ip) {
  const m = IPV4_RE.exec(String(ip).trim());
  if (!m) return null;
  return `${m[4]}.${m[3]}.${m[2]}.${m[1]}`;
}

// Non-routable / special-use IPv4 space, per IANA. Order matters: the most
// specific ranges are listed first so the first hit is the best label.
const RESERVED_V4 = [
  { cidr: '0.0.0.0/8',          label: 'This network',        note: 'Unspecified / "this host" space (RFC 1122). Not a real source address.' },
  { cidr: '10.0.0.0/8',         label: 'Private (RFC 1918)',  note: 'Internal network address. Behind NAT — the public IP will be different, and identifying the device requires the network owner’s internal DHCP or NAT logs.' },
  { cidr: '100.64.0.0/10',      label: 'CGNAT (RFC 6598)',    note: 'Carrier-grade NAT. Shared by many subscribers of the same ISP — subscriber identification needs the ISP’s NAT logs including the source port and exact timestamp.' },
  { cidr: '127.0.0.0/8',        label: 'Loopback',            note: 'The machine itself. Never appears as a genuine remote address.' },
  { cidr: '169.254.0.0/16',     label: 'Link-local (APIPA)',  note: 'Self-assigned when DHCP failed. Local network segment only.' },
  { cidr: '172.16.0.0/12',      label: 'Private (RFC 1918)',  note: 'Internal network address. Behind NAT — the public IP will be different, and identifying the device requires the network owner’s internal DHCP or NAT logs.' },
  { cidr: '192.0.0.0/24',       label: 'IETF protocol space', note: 'Reserved for protocol assignments (RFC 6890).' },
  { cidr: '192.0.2.0/24',       label: 'Documentation',       note: 'TEST-NET-1, reserved for examples (RFC 5737). Often a placeholder in redacted records.' },
  { cidr: '192.88.99.0/24',     label: '6to4 relay anycast',  note: 'Deprecated 6to4 relay range (RFC 7526).' },
  { cidr: '192.168.0.0/16',     label: 'Private (RFC 1918)',  note: 'Internal network address — the common home/office LAN range. Behind NAT — the public IP will be different, and identifying the device requires the network owner’s internal DHCP or NAT logs.' },
  { cidr: '198.18.0.0/15',      label: 'Benchmarking',        note: 'Reserved for network benchmark testing (RFC 2544).' },
  { cidr: '198.51.100.0/24',    label: 'Documentation',       note: 'TEST-NET-2, reserved for examples (RFC 5737). Often a placeholder in redacted records.' },
  { cidr: '203.0.113.0/24',     label: 'Documentation',       note: 'TEST-NET-3, reserved for examples (RFC 5737). Often a placeholder in redacted records.' },
  { cidr: '224.0.0.0/4',        label: 'Multicast',           note: 'Multicast group address, not an individual host.' },
  { cidr: '255.255.255.255/32', label: 'Broadcast',           note: 'Limited broadcast address.' },
  { cidr: '240.0.0.0/4',        label: 'Reserved',            note: 'Reserved for future use (RFC 1112).' },
].map(r => ({ ...r, range: cidrToRange(r.cidr) }));

// Returns { label, note, cidr } when the address is special-use, else null.
// A non-null result means live lookups are pointless — nothing authoritative
// exists to look up — so the tool should skip them entirely.
export function classifyReserved(ip) {
  const s = String(ip).trim();

  if (isValidIpv6(s)) {
    const low = s.toLowerCase();
    if (low === '::1') return { label: 'Loopback', note: 'The machine itself. Never a genuine remote address.', cidr: '::1/128' };
    if (low === '::') return { label: 'Unspecified', note: 'The unspecified address (RFC 4291).', cidr: '::/128' };
    if (/^f[cd]/.test(low)) return { label: 'Unique local (RFC 4193)', note: 'IPv6 private address space, analogous to RFC 1918. Internal to one organization.', cidr: 'fc00::/7' };
    if (/^fe[89ab]/.test(low)) return { label: 'Link-local', note: 'IPv6 link-local address — valid only on the local network segment.', cidr: 'fe80::/10' };
    if (/^ff/.test(low)) return { label: 'Multicast', note: 'IPv6 multicast group, not an individual host.', cidr: 'ff00::/8' };
    if (low.startsWith('2001:db8')) return { label: 'Documentation', note: 'Reserved for examples (RFC 3849). Often a placeholder in redacted records.', cidr: '2001:db8::/32' };
    return null;
  }

  const n = ipToUint32(s);
  if (n === null) return null;
  for (const r of RESERVED_V4) {
    if (r.range && n >= r.range[0] && n <= r.range[1]) {
      return { label: r.label, note: r.note, cidr: r.cidr };
    }
  }
  return null;
}

// Pull every IP out of arbitrary pasted text: one per line, comma-separated,
// CSV columns, log lines, "IP:port", or bracketed IPv6. Order is preserved and
// duplicates are dropped so a 300-line log collapses to the distinct hosts.
export function parseIpList(text) {
  const seen = new Set();
  const out = [];
  let duplicateCount = 0;

  // One pass, so the output keeps the order the addresses appeared in and each
  // textual occurrence is counted exactly once. Brackets are deliberately NOT
  // split on — they're unwrapped per token, which keeps "[2001:db8::1]:443" in
  // its original position instead of hoisting it to the front.
  for (const rawToken of String(text || '').split(/[\s,;"'|<>(){}]+/)) {
    if (!rawToken) continue;

    let token;
    const bracketed = /^\[([^\]]+)\]/.exec(rawToken);
    if (bracketed) {
      token = bracketed[1];
    } else {
      token = rawToken.replace(/[.,;:[\]]+$/, '').replace(/^[[\]]+/, '');
      token = token.split('/')[0];                 // CIDR -> network address
      // A trailing ":<port>" is stripped from v4 only — in v6 the colons are
      // the address, so a bare v6 with a port can't be disambiguated.
      if (/^\d{1,3}(\.\d{1,3}){3}:\d{1,5}$/.test(token)) token = token.split(':')[0];
    }

    if (!isValidIp(token)) continue;
    const key = token.toLowerCase();
    if (seen.has(key)) { duplicateCount++; continue; }
    seen.add(key);
    out.push(token);
  }

  return { ips: out, duplicateCount };
}
